import type { VoiceUserState } from '@/types/api';
import type { DataState } from './dataStore';

/**
 * Pure voice-room reducers, extracted from the store so the (subtle,
 * race-condition-laden) logic is unit-testable in isolation. Each takes
 * the current state plus the resolved local user id (`meId`) and returns a
 * spread-compatible partial. The store wires `meId` from authStore.
 */

/** Apply a single user's voice state change (joined / state-change). */
export function applyVoiceUserStateReducer(
  s: DataState,
  state: VoiceUserState,
  meId: string | null,
): Partial<DataState> | DataState {
  // Performance: only the channels that actually CHANGED get a new
  // array reference. Other channels keep their old reference so
  // selectors on unrelated rooms ({channelId: ...}) don't invalidate.
  // Previously we rebuilt every entry of voiceByChannel on every
  // mute/unmute push, which cascaded a re-render through every voice
  // tile in the app.
  const next: Record<string, VoiceUserState[]> = { ...s.voiceByChannel };
  let changed = false;
  // 1. Drop the user from any OTHER channel they were sitting in.
  //    Only rewrite the array when the user was actually present.
  for (const cid of Object.keys(s.voiceByChannel)) {
    if (cid === state.channelId) continue;
    const room = s.voiceByChannel[cid]!;
    const idx = room.findIndex((u) => u.userId === state.userId);
    if (idx < 0) continue;
    const filtered = room.filter((u) => u.userId !== state.userId);
    if (filtered.length > 0) next[cid] = filtered;
    else delete next[cid];
    changed = true;
  }
  // 2. Upsert into the target channel.
  const targetRoom = s.voiceByChannel[state.channelId] ?? [];
  const existing = targetRoom.find((u) => u.userId === state.userId) ?? null;
  const isSameState =
    existing &&
    existing.selfMute === state.selfMute &&
    existing.selfDeaf === state.selfDeaf &&
    existing.serverMute === state.serverMute &&
    existing.serverDeaf === state.serverDeaf &&
    existing.videoOn === state.videoOn &&
    existing.screenOn === state.screenOn &&
    existing.joinedAt === state.joinedAt;
  if (!isSameState) {
    next[state.channelId] = [
      ...targetRoom.filter((u) => u.userId !== state.userId),
      state,
    ];
    changed = true;
  }
  // 3. Sync myVoiceChannelId when the change is about us.
  const myVoiceChannelId =
    meId && state.userId === meId ? state.channelId : s.myVoiceChannelId;
  const myChanged = myVoiceChannelId !== s.myVoiceChannelId;
  if (!changed && !myChanged) return s;
  return {
    voiceByChannel: changed ? next : s.voiceByChannel,
    myVoiceChannelId,
  };
}

/** Remove a user from any voice room they were in. */
export function removeVoiceUserReducer(
  s: DataState,
  userId: string,
  channelId: string,
  joinedAt: number | null | undefined,
  meId: string | null,
): Partial<DataState> | DataState {
  const room = s.voiceByChannel[channelId];
  if (!room) {
    // Edge case: the room was already purged from voiceByChannel
    // (e.g. removeChannel/removeGuild ran before this echo arrived).
    // We still need to clear `myVoiceChannelId` if the leave is
    // ours — otherwise the UI thinks we're in a room that no longer
    // exists in the map. Without this fix the early-return left us
    // glued to a ghost channel id.
    if (meId && userId === meId && s.myVoiceChannelId === channelId) {
      return { myVoiceChannelId: null };
    }
    return s;
  }
  const current = room.find((u) => u.userId === userId) ?? null;
  // Stale-event guard: ignore the leave if our cached `joinedAt` is
  // newer than the incoming one (a rapid leave+rejoin can deliver
  // events out of order over Socket.io with unreliable transport
  // fallback).
  if (current && typeof joinedAt === 'number' && current.joinedAt > joinedAt) {
    return s;
  }
  const filtered = room.filter((u) => u.userId !== userId);
  const next = { ...s.voiceByChannel };
  if (filtered.length === 0) delete next[channelId];
  else next[channelId] = filtered;
  // Only clear our own myVoiceChannelId when we have a CURRENT
  // record and the stamps match. Previously the OR-with-`??` allowed
  // `null current` to null out the field via the right-hand
  // tautology, which yanked the user out of voice on a stale leave.
  const isMyExactLeave =
    meId &&
    userId === meId &&
    current !== null &&
    (typeof joinedAt !== 'number' || current.joinedAt === joinedAt);
  const myVoiceChannelId = isMyExactLeave ? null : s.myVoiceChannelId;
  return { voiceByChannel: next, myVoiceChannelId };
}
